import { prepareAssignment } from './assignmentPreview';
/**
 * Email Address Service
 *
 * Handles API calls for managing inbound email addresses for receiving
 * travel confirmations via SendGrid Inbound Parse.
 */

import axios from 'axios';
import { getApiBase, getAuthHeaders } from '../utils/api';

/**
 * Create a new inbound email address
 * @param {Object} params - Email address parameters
 * @param {string} params.email_type - Type: "executive" or "trip"
 * @param {string} [params.executive_id] - Executive ID (required for executive type)
 * @param {string} [params.trip_id] - Trip ID (required for trip type)
 * @param {boolean} [params.auto_parse=true] - Auto-parse attachments
 * @param {string} [params.custom_email_prefix] - Custom email prefix for executive type
 * @returns {Promise<Object>} Created email address
 */
export async function createEmailAddress({ email_type, executive_id, trip_id, auto_parse = true, custom_email_prefix }) {
  const apiBase = getApiBase();
  const headers = getAuthHeaders();

  // Create FormData for multipart/form-data request
  const formData = new FormData();
  formData.append('email_type', email_type);
  if (executive_id) formData.append('executive_id', executive_id);
  if (trip_id) formData.append('trip_id', trip_id);
  formData.append('auto_parse', auto_parse.toString());
  if (custom_email_prefix) formData.append('custom_email_prefix', custom_email_prefix);

  const response = await axios.post(
    `${apiBase}/inbound-email-addresses`,
    formData,
    {
      headers: {
        ...headers,
        'Content-Type': 'multipart/form-data',
      },
    }
  );

  return response.data;
}

/**
 * Suggest an email address for an executive based on their name
 * @param {string} executiveId - Executive ID
 * @returns {Promise<Object>} Suggested email address details
 */
export async function suggestEmailAddress(executiveId) {
  const apiBase = getApiBase();
  const headers = getAuthHeaders();

  const response = await axios.get(
    `${apiBase}/executives/${executiveId}/suggest-email`,
    { headers }
  );

  return response.data;
}

/**
 * Check if an email prefix is available
 * @param {string} emailPrefix - The prefix to check (e.g., 'jsmith-travel')
 * @returns {Promise<Object>} Availability status and suggestion if taken
 */
export async function checkEmailAvailability(emailPrefix) {
  const apiBase = getApiBase();
  const headers = getAuthHeaders();

  const response = await axios.get(
    `${apiBase}/inbound-email-addresses/check-availability?email_prefix=${encodeURIComponent(emailPrefix)}`,
    { headers }
  );

  return response.data;
}

/**
 * List inbound email addresses
 * @param {Object} [filters] - Filter options
 * @param {string} [filters.email_type] - Filter by type
 * @param {string} [filters.executive_id] - Filter by executive
 * @param {string} [filters.trip_id] - Filter by trip
 * @returns {Promise<Object>} List of email addresses
 */
export async function listEmailAddresses(filters = {}) {
  const apiBase = getApiBase();
  const headers = getAuthHeaders();

  const params = new URLSearchParams();
  if (filters.email_type) params.append('email_type', filters.email_type);
  if (filters.executive_id) params.append('executive_id', filters.executive_id);
  if (filters.trip_id) params.append('trip_id', filters.trip_id);

  const url = `${apiBase}/inbound-email-addresses${params.toString() ? `?${params.toString()}` : ''}`;

  const response = await axios.get(url, { headers });
  return response.data;
}

/**
 * Get a specific email address
 * @param {string} emailId - Email address ID
 * @returns {Promise<Object>} Email address details
 */
export async function getEmailAddress(emailId) {
  const apiBase = getApiBase();
  const headers = getAuthHeaders();

  const response = await axios.get(
    `${apiBase}/inbound-email-addresses/${emailId}`,
    { headers }
  );

  return response.data;
}

/**
 * Update an email address
 * @param {string} emailId - Email address ID
 * @param {Object} updates - Fields to update
 * @param {boolean} [updates.is_active] - Active status
 * @param {boolean} [updates.auto_parse] - Auto-parse setting
 * @returns {Promise<Object>} Updated email address
 */
export async function updateEmailAddress(emailId, updates) {
  const apiBase = getApiBase();
  const headers = getAuthHeaders();

  const formData = new FormData();
  if (updates.is_active !== undefined) formData.append('is_active', updates.is_active.toString());
  if (updates.auto_parse !== undefined) formData.append('auto_parse', updates.auto_parse.toString());

  const response = await axios.patch(
    `${apiBase}/inbound-email-addresses/${emailId}`,
    formData,
    {
      headers: {
        ...headers,
        'Content-Type': 'multipart/form-data',
      },
    }
  );

  return response.data;
}

/**
 * Delete an email address
 * @param {string} emailId - Email address ID
 * @returns {Promise<Object>} Deletion result
 */
export async function deleteEmailAddress(emailId) {
  const apiBase = getApiBase();
  const headers = getAuthHeaders();

  const response = await axios.delete(
    `${apiBase}/inbound-email-addresses/${emailId}`,
    { headers }
  );

  return response.data;
}

/**
 * List unfilled components
 * @param {string} [executiveId] - Filter by executive ID
 * @param {boolean} [unassignedOnly] - Filter to only unassigned items (EA inbox items without an executive)
 * @returns {Promise<Object>} List of unfilled components
 */
export async function listUnfilledComponents(executiveId, unassignedOnly = false, includeShared = false) {
  const apiBase = getApiBase();
  const headers = getAuthHeaders();

  const params = new URLSearchParams();
  if (executiveId) params.append('executive_id', executiveId);
  if (unassignedOnly) params.append('unassigned_only', 'true');
  if (includeShared) params.append('include_shared', 'true');

  const queryString = params.toString();
  const url = queryString
    ? `${apiBase}/unfilled-components?${queryString}`
    : `${apiBase}/unfilled-components`;

  const response = await axios.get(url, { headers });
  return response.data;
}

/**
 * Assign an unfilled component to a trip
 * @param {string} componentId - Component ID
 * @param {string} tripId - Trip ID
 * @returns {Promise<Object>} Assignment result
 */
export async function assignUnfilledComponent(componentId, tripId, acceptedPreview) {
  const apiBase = getApiBase();
  const headers = getAuthHeaders();

  const formData = new FormData();
  formData.append('trip_id', tripId);
  const preview = acceptedPreview || await prepareAssignment([componentId], tripId);
  formData.append('preview_token', preview.preview_token);
  formData.append('decisions', JSON.stringify(preview.decisions));

  const response = await axios.post(
    `${apiBase}/unfilled-components/${componentId}/assign`,
    formData,
    {
      headers: {
        ...headers,
        'Content-Type': 'multipart/form-data',
      },
    }
  );

  return response.data;
}

/**
 * Bulk assign multiple unfilled components to a single trip
 * @param {string[]} componentIds - Array of component IDs
 * @param {string} tripId - Trip ID
 * @returns {Promise<Object>} Assignment result
 */
export async function bulkAssignUnfilledComponents(componentIds, tripId) {
  const apiBase = getApiBase();
  const headers = getAuthHeaders();

  const preview = await prepareAssignment(componentIds, tripId);
  const response = await axios.post(
    `${apiBase}/unfilled-components/bulk-assign`,
    {
      component_ids: componentIds,
      trip_id: tripId,
      ...preview
    },
    { headers }
  );

  return response.data;
}

/**
 * Delete an unfilled component
 * @param {string} componentId - Component ID
 * @returns {Promise<Object>} Deletion result
 */
export async function deleteUnfilledComponent(componentId) {
  const apiBase = getApiBase();
  const headers = getAuthHeaders();

  const response = await axios.delete(
    `${apiBase}/unfilled-components/${componentId}`,
    { headers }
  );

  return response.data;
}

/**
 * File an unfilled component (flexible assignment)
 *
 * This endpoint supports:
 * - Assigning to executive only (moves from EA inbox to executive's unfiled items)
 * - Assigning to trip only (moves to trip components)
 * - Assigning to both executive AND trip (sets executive, then moves to trip)
 *
 * @param {string} componentId - Component ID
 * @param {Object} options - Assignment options
 * @param {string} [options.executiveId] - Executive ID (optional)
 * @param {string} [options.tripId] - Trip ID (optional)
 * @returns {Promise<Object>} Filing result
 */
export async function fileUnfilledComponent(componentId, { executiveId, tripId, reconcileComponentId, acceptedPreview }) {
  const apiBase = getApiBase();
  const headers = getAuthHeaders();

  const body = {};
  if (executiveId) body.executive_id = executiveId;
  if (reconcileComponentId) body.reconcile_component_id = reconcileComponentId;
  if (tripId) {
    body.trip_id = tripId;
    Object.assign(body, acceptedPreview || await prepareAssignment([componentId], tripId, executiveId, reconcileComponentId));
  }

  const response = await axios.post(
    `${apiBase}/unfilled-components/${componentId}/file`,
    body,
    { headers }
  );

  return response.data;
}

/**
 * List parsing logs (includes both email and document imports)
 * @param {Object} [params] - Query parameters
 * @param {string} [params.inbound_email_address_id] - Filter by email address (email logs only)
 * @param {string} [params.source_type] - Filter by source type ('email' or 'document_import')
 * @param {number} [params.limit=50] - Maximum number of logs
 * @returns {Promise<Object>} List of logs
 */
export async function listParsingLogs({ inbound_email_address_id, source_type, limit = 50 } = {}) {
  const apiBase = getApiBase();
  const headers = getAuthHeaders();

  const params = new URLSearchParams();
  if (inbound_email_address_id) params.append('inbound_email_address_id', inbound_email_address_id);
  if (source_type) params.append('source_type', source_type);
  params.append('limit', limit.toString());

  const response = await axios.get(
    `${apiBase}/parsing-logs?${params.toString()}`,
    { headers }
  );

  return response.data;
}

/**
 * Create a parsing feedback entry directly from a parsing log.
 *
 * @param {string} logId - Parsing log ID
 * @param {Object} payload - Feedback payload
 * @param {number[]} [payload.confirmation_indexes] - Indexes into ai_parsed_confirmations (empty = all)
 * @param {string[]} [payload.error_categories] - Error categories
 * @param {string[]} [payload.affected_fields] - Affected fields
 * @param {string} [payload.user_description] - User description of the issue
 * @param {Object} [payload.expected_values] - Expected values (optional)
 * @param {string} [payload.priority] - Priority (low/medium/high/critical)
 * @returns {Promise<Object>} Created feedback info
 */
export async function createParsingFeedbackFromLog(logId, payload = {}) {
  const apiBase = getApiBase();
  const headers = getAuthHeaders();

  const response = await axios.post(
    `${apiBase}/parsing-logs/${logId}/feedback`,
    payload,
    { headers }
  );

  return response.data;
}

/**
 * Run the parsing-feedback GitHub integration diagnostic on the backend.
 * Returns pass/fail for each check (system settings, GITHUB_TOKEN presence,
 * github_feedback_commit module import, token auth, repo read, contents read).
 * Requires the parsing_feedback admin permission.
 *
 * @returns {Promise<Object>} {all_ok: boolean, checks: {...}}
 */
export async function getParsingFeedbackGithubDiagnostic() {
  const apiBase = getApiBase();
  const headers = getAuthHeaders();

  const response = await axios.get(
    `${apiBase}/admin/parsing-feedback/github-diagnostic`,
    { headers }
  );

  return response.data;
}

/**
 * List inbound email logs (deprecated, use listParsingLogs)
 * @param {Object} [params] - Query parameters
 * @param {string} [params.inbound_email_address_id] - Filter by email address
 * @param {number} [params.limit=50] - Maximum number of logs
 * @returns {Promise<Object>} List of logs
 */
export async function listEmailLogs({ inbound_email_address_id, limit = 50 } = {}) {
  const apiBase = getApiBase();
  const headers = getAuthHeaders();

  const params = new URLSearchParams();
  if (inbound_email_address_id) params.append('inbound_email_address_id', inbound_email_address_id);
  params.append('limit', limit.toString());

  const response = await axios.get(
    `${apiBase}/inbound-email-logs?${params.toString()}`,
    { headers }
  );

  return response.data;
}

// =========================
// EA Personal Email Functions
// =========================

/**
 * Get suggested EA email prefixes for a user
 * @param {string} userId - User ID
 * @returns {Promise<Object>} Suggested prefixes for standard and confidential emails
 */
export async function getEAEmailSuggestions(userId) {
  const apiBase = getApiBase();
  const headers = getAuthHeaders();

  const response = await axios.get(
    `${apiBase}/users/${userId}/ea-emails/suggestions`,
    { headers }
  );

  return response.data;
}

/**
 * Get a user's EA email addresses
 * @param {string} userId - User ID
 * @returns {Promise<Object>} User's standard and confidential email addresses
 */
export async function getUserEAEmails(userId) {
  const apiBase = getApiBase();
  const headers = getAuthHeaders();

  const response = await axios.get(
    `${apiBase}/users/${userId}/ea-emails`,
    { headers }
  );

  return response.data;
}

/**
 * Allocate EA email addresses for a user
 * @param {string} userId - User ID
 * @param {Object} options - Allocation options
 * @param {boolean} [options.allocateStandard=true] - Whether to allocate standard email
 * @param {boolean} [options.allocateConfidential=true] - Whether to allocate confidential email
 * @param {string} [options.standardPrefix] - Custom prefix for standard email
 * @param {string} [options.confidentialPrefix] - Custom prefix for confidential email
 * @returns {Promise<Object>} Allocated email addresses
 */
export async function allocateEAEmails(userId, {
  allocateStandard = true,
  allocateConfidential = true,
  standardPrefix,
  confidentialPrefix
} = {}) {
  const apiBase = getApiBase();
  const headers = getAuthHeaders();

  const formData = new FormData();
  formData.append('allocate_standard', allocateStandard.toString());
  formData.append('allocate_confidential', allocateConfidential.toString());
  if (standardPrefix) formData.append('standard_prefix', standardPrefix);
  if (confidentialPrefix) formData.append('confidential_prefix', confidentialPrefix);

  const response = await axios.post(
    `${apiBase}/users/${userId}/ea-emails`,
    formData,
    {
      headers: {
        ...headers,
        'Content-Type': 'multipart/form-data',
      },
    }
  );

  return response.data;
}

/**
 * Delete an EA email address
 * @param {string} userId - User ID
 * @param {string} emailType - Type: "standard" or "confidential"
 * @returns {Promise<Object>} Deletion result
 */
export async function deleteEAEmail(userId, emailType) {
  const apiBase = getApiBase();
  const headers = getAuthHeaders();

  const response = await axios.delete(
    `${apiBase}/users/${userId}/ea-emails/${emailType}`,
    { headers }
  );

  return response.data;
}

/**
 * Update an EA email prefix
 * @param {string} userId - User ID
 * @param {string} emailType - Type: "standard" or "confidential"
 * @param {string} newPrefix - New email prefix
 * @returns {Promise<Object>} Updated email address
 */
export async function updateEAEmailPrefix(userId, emailType, newPrefix) {
  const apiBase = getApiBase();
  const headers = getAuthHeaders();

  const formData = new FormData();
  formData.append('new_prefix', newPrefix);

  const response = await axios.patch(
    `${apiBase}/users/${userId}/ea-emails/${emailType}`,
    formData,
    {
      headers: {
        ...headers,
        'Content-Type': 'multipart/form-data',
      },
    }
  );

  return response.data;
}

/**
 * Get all EA email allocations for the organization (admin only)
 * @returns {Promise<Object>} List of all EA email allocations
 */
export async function getAllEAEmailAllocations() {
  const apiBase = getApiBase();
  const headers = getAuthHeaders();

  const response = await axios.get(
    `${apiBase}/ea-email-allocations`,
    { headers }
  );

  return response.data;
}

/**
 * Get the unified email directory for the organization (admin only)
 * Shows all email addresses: user logins, executive emails, and inbound routing addresses
 * @returns {Promise<Object>} Email directory with all addresses and their linkage info
 */
export async function getEmailDirectory() {
  const apiBase = getApiBase();
  const headers = getAuthHeaders();

  const response = await axios.get(
    `${apiBase}/admin/email-directory`,
    { headers }
  );

  return response.data;
}

/**
 * Check if an EA email prefix is available
 * @param {string} prefix - Email prefix to check
 * @returns {Promise<Object>} Availability status
 */
export async function checkEAEmailAvailability(prefix) {
  const apiBase = getApiBase();
  const headers = getAuthHeaders();

  const response = await axios.get(
    `${apiBase}/ea-emails/check-availability?prefix=${encodeURIComponent(prefix)}`,
    { headers }
  );

  return response.data;
}
